import React from 'react';

interface UpSetPlotProps {
  sets: Record<string, string[]>; // group name -> array of gene names
  width?: number;
  height?: number;
}

// Utility: compute all intersections and their sizes
function computeIntersections(sets: Record<string, string[]>) {
  const groupNames = Object.keys(sets);
  const geneToGroups: Record<string, Set<string>> = {};
  groupNames.forEach(group => {
    sets[group].forEach(gene => {
      if (!geneToGroups[gene]) geneToGroups[gene] = new Set();
      geneToGroups[gene].add(group);
    });
  });
  // Map: intersection key (sorted group names joined by ",") -> Set of genes
  const intersectionMap: Record<string, Set<string>> = {};
  Object.entries(geneToGroups).forEach(([gene, groups]) => {
    const key = Array.from(groups).sort().join(',');
    if (!intersectionMap[key]) intersectionMap[key] = new Set();
    intersectionMap[key].add(gene);
  });
  // Convert to array for plotting
  const intersections = Object.entries(intersectionMap).map(([key, genes]) => ({
    groups: key.split(','),
    size: genes.size,
    genes: Array.from(genes),
  }));
  // Sort by size descending
  intersections.sort((a, b) => b.size - a.size);
  return { groupNames, intersections };
}

const UpSetPlot: React.FC<UpSetPlotProps> = ({ sets, width = 700, height = 340 }) => {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = React.useState(width);

  // Responsive: update width on resize
  React.useEffect(() => {
    function updateWidth() {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    }
    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  const { groupNames, intersections } = React.useMemo(() => computeIntersections(sets), [sets]);
  const maxSize = Math.max(...intersections.map(i => i.size), 1);
  const barMaxHeight = Math.max(80, height - 80); // ensure bars are not too tall and always leave a gap above and below
  const barWidth = 32;
  const gap = 18;
  const leftMargin = 120; // ensure space for group labels
  const plotWidth = intersections.length * (barWidth + gap);
  // For group matrix
  const matrixY0 = height - 80; // keep dots at a reasonable distance from the bottom
  const matrixCell = 18;
  const bottomPadding = 40; // extra space for bottom labels

  // Tooltip state
  const [tooltip, setTooltip] = React.useState<null | { x: number; y: number; genes: string[]; groups: string[] }>(null);
  // Track which column is hovered for highlight
  const [hoveredCol, setHoveredCol] = React.useState<number | null>(null);

  return (
    <>
      <div
        ref={containerRef}
        style={{ width: '100%', minWidth: 400, overflowX: 'auto', background: '#fff', borderRadius: 12, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', padding: 24, margin: '32px auto', position: 'relative' }}
      >
        <h2 style={{ fontSize: 22, color: '#2a2a4a', fontWeight: 700, marginBottom: 18 }}>UpSet Plot: Top Gene Set Intersections</h2>
        <svg
          width={Math.max(containerWidth, plotWidth + leftMargin + 40)}
          height={height + bottomPadding}
          style={{ display: 'block', width: '100%', minWidth: plotWidth + leftMargin + 40, maxWidth: 'none', overflow: 'visible' }}
        >
          {/* Bars */}
          {intersections.map((int, i) => (
            <g key={i}>
              <rect
                x={leftMargin + i * (barWidth + gap)}
                y={matrixY0 - (int.size / maxSize) * barMaxHeight}
                width={barWidth}
                height={(int.size / maxSize) * barMaxHeight}
                fill="#4a90e2"
                rx={5}
                style={{ transition: 'height 0.2s', cursor: 'pointer',
                  stroke: hoveredCol === i ? '#1a237e' : '#2a2a4a',
                  strokeWidth: hoveredCol === i ? 3 : 1.5 }}
                onMouseEnter={e => {
                  setTooltip({
                    x: e.clientX,
                    y: e.clientY,
                    genes: int.genes,
                    groups: int.groups
                  });
                  setHoveredCol(i);
                }}
                onMouseMove={e => {
                  setTooltip(t => t ? { ...t, x: e.clientX, y: e.clientY } : null);
                }}
                onMouseLeave={() => {
                  setTooltip(null);
                  setHoveredCol(null);
                }}
              />
              {/* Size label */}
              <text
                x={leftMargin + i * (barWidth + gap) + barWidth / 2}
                y={matrixY0 - (int.size / maxSize) * barMaxHeight - 8}
                textAnchor="middle"
                fontSize={14}
                fill="#2a2a4a"
                fontWeight={600}
              >
                {int.size}
              </text>
              {/* Render group circles with highlight on hover */}
              {groupNames.map((g, j) => {
                const isActive = int.groups.includes(g);
                const isHovered = hoveredCol === i;
                return (
                  <circle
                    key={g}
                    cx={leftMargin + i * (barWidth + gap) + barWidth / 2}
                    cy={matrixY0 + j * matrixCell}
                    r={isActive ? 7 : 4.9}
                    fill={isActive ? '#e94f37' : '#e0e0e0'}
                    stroke={isActive ? (isHovered ? '#7b1fa2' : '#b71c1c') : (isHovered ? '#424242' : '#fff')}
                    strokeWidth={isHovered ? 2.5 : (isActive ? 2.1 : 1.4)}
                    style={{ transition: 'all 0.15s', cursor: 'pointer' }}
                    onMouseEnter={e => {
                      setTooltip({
                        x: e.clientX,
                        y: e.clientY,
                        genes: int.genes,
                        groups: int.groups
                      });
                      setHoveredCol(i);
                    }}
                    onMouseMove={e => {
                      setTooltip(t => t ? { ...t, x: e.clientX, y: e.clientY } : null);
                    }}
                    onMouseLeave={() => {
                      setTooltip(null);
                      setHoveredCol(null);
                    }}
                  />
                );
              })}
            </g>
          ))}
          {/* Group labels (fully visible inside chart) */}
          {groupNames.map((g, j) => (
            <text
              key={g}
              x={leftMargin - 10}
              y={matrixY0 + j * matrixCell + 5}
              fontSize={15}
              textAnchor="end"
              fill="#2a2a4a"
              fontWeight={600}
              style={{ background: '#fff' }}
            >
              {g}
            </text>
          ))}
        </svg>
        {/* Tooltip for bar genes and intersecting groups */}
        {tooltip && (
          <div
            style={{
              position: 'fixed',
              left: tooltip.x + 16,
              top: tooltip.y + 12,
              background: 'rgba(30,30,30,0.97)',
              color: '#fff',
              padding: '12px 18px',
              borderRadius: 8,
              fontSize: 15,
              pointerEvents: 'none',
              zIndex: 100,
              minWidth: 180,
              maxWidth: 420,
              maxHeight: 320,
              overflowY: 'auto',
              userSelect: 'none',
              WebkitUserSelect: 'none',
              MozUserSelect: 'none',
              msUserSelect: 'none',
              boxShadow: '0 2px 12px rgba(0,0,0,0.18)',
              fontWeight: 500,
              whiteSpace: 'normal',
            }}
          >
            <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 6 }}>Genes in this intersection ({tooltip.genes.length}):</div>
            <div style={{ fontSize: 14, lineHeight: 1.5, wordBreak: 'break-all', marginBottom: 10 }}>
              {tooltip.genes.join(', ')}
            </div>
            <div style={{ fontWeight: 600, fontSize: 15, marginTop: 8, marginBottom: 2 }}>Intersecting groups:</div>
            <div style={{ fontSize: 14, lineHeight: 1.5, wordBreak: 'break-all' }}>
              {tooltip.groups.join(', ')}
            </div>
          </div>
        )}
      </div>
      <div style={{ fontSize: 15, color: '#444', margin: '18px auto 0 auto', textAlign: 'center', maxWidth: 900, background: '#f7f8fa', borderRadius: 9, padding: 14, boxShadow: '0 1px 4px rgba(0,0,0,0.04)', fontWeight: 500 }}>
        <b>Note:</b> Each bar shows the number of genes in the intersection of the highlighted groups. Dots below indicate which groups are in each intersection.
      </div>
    </>
  );
};

export default UpSetPlot;
